import type { SlackInstallation } from "@mono/api-contracts";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { AlertTriangle, CheckCircle2, ExternalLink, MessageCircle, X } from "lucide-react";
import { useState } from "react";

import {
  disconnectSlackInstallation,
  getQuickBooksStatus,
  getSlackConnectUrl,
  listSlackInstallations,
} from "@/features/integrations/api/integrations";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/ui";

type IntegrationsSearch = { error?: string };

export const Route = createFileRoute("/_authenticated/integrations/")({
  component: IntegrationsPage,
  validateSearch: (search: Record<string, unknown>): IntegrationsSearch => {
    return {
      error: typeof search.error === "string" ? search.error : undefined,
    };
  },
});

function IntegrationsPage() {
  const [showError, setShowError] = useState(true);
  const navigate = useNavigate();

  // Route.useSearch() is properly typed by validateSearch, but TypeScript inference
  // may not work until route tree is regenerated
  const searchParams = Route.useSearch() as unknown as IntegrationsSearch;
  const { error } = searchParams;

  const handleCloseError = () => {
    setShowError(false);
    // Clear the error from the URL by navigating to the same route without the error param
    void navigate({ to: "." });
  };

  return (
    <div className="p-4 sm:p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Integrations</h1>
          <p className="mt-1 text-sm text-muted-foreground">Manage your data source integrations</p>
        </div>
      </div>

      {error && showError && (
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 flex items-start justify-between gap-4">
          <p className="text-sm text-destructive">{String(error)}</p>
          <button
            onClick={handleCloseError}
            className="text-destructive hover:text-destructive/80 transition-colors flex-shrink-0"
            aria-label="Close error message"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <QuickBooksCard />
        <SlackCard />
      </div>
    </div>
  );
}

function SlackCard() {
  const [isConnecting, setIsConnecting] = useState(false);
  const [pendingDisconnect, setPendingDisconnect] = useState<SlackInstallation | null>(null);
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["slack-installations"],
    queryFn: listSlackInstallations,
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  });

  const installations = data?.installations ?? [];
  const activeInstallations = installations.filter((installation) => installation.status === "active");
  const revokedInstallations = installations.filter((installation) => installation.status === "revoked");

  const disconnectMutation = useMutation({
    mutationFn: disconnectSlackInstallation,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["slack-installations"] });
      setPendingDisconnect(null);
    },
  });

  const handleConnect = (reconnectTeamId?: string) => {
    setIsConnecting(true);
    window.location.href = getSlackConnectUrl({
      redirect: "/integrations",
      reconnectTeamId,
    });
  };

  return (
    <Card className="border-l-4 border-l-[#4A154B]">
      <CardHeader>
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-[#4A154B] text-white">
              <MessageCircle className="size-5" />
            </div>
            <div>
              <CardTitle>Slack</CardTitle>
              <CardDescription>Receive mono insights in Slack DMs</CardDescription>
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-muted-foreground">
          Install the Slack app to use mono in direct messages with your workspace data context.
        </p>
        {!isLoading && (
          <div className="mt-4 space-y-3">
            {installations.length === 0 && (
              <div className="rounded-lg border bg-muted/30 p-3 text-sm text-muted-foreground">
                No Slack workspaces connected yet.
              </div>
            )}

            {installations.map((installation) => {
              const teamLabel = installation.teamName || installation.teamId;
              const isRevoked = installation.status === "revoked";
              return (
                <div
                  key={installation.teamId}
                  className="rounded-lg border bg-muted/30 p-3 flex items-start justify-between gap-3"
                >
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium">{teamLabel}</span>
                      <Badge variant={isRevoked ? "outline" : "default"}>
                        {isRevoked ? "Revoked" : "Active"}
                      </Badge>
                    </div>
                    <p className="text-xs text-muted-foreground">Team ID: {installation.teamId}</p>
                    {isRevoked && (
                      <p className="text-xs text-amber-700">
                        {installation.revokedReason
                          ? `Reconnect required (${installation.revokedReason}).`
                          : "Reconnect required."}
                      </p>
                    )}
                  </div>

                  <div className="flex flex-col gap-2">
                    {isRevoked ? (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleConnect(installation.teamId)}
                        disabled={isConnecting}
                      >
                        Reconnect
                        <ExternalLink className="ml-2 size-3" />
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setPendingDisconnect(installation)}
                        disabled={disconnectMutation.isPending}
                      >
                        Disconnect
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {!isLoading && installations.length > 0 && (
          <div className="mt-4 flex items-center gap-4 text-xs text-muted-foreground">
            <span className="flex items-center gap-1">
              <CheckCircle2 className="size-3 text-emerald-600" />
              Active: {activeInstallations.length}
            </span>
            <span className="flex items-center gap-1">
              <AlertTriangle className="size-3 text-amber-600" />
              Revoked: {revokedInstallations.length}
            </span>
          </div>
        )}
      </CardContent>
      <CardFooter>
        <Button
          variant="default"
          className="w-full"
          onClick={() => handleConnect()}
          disabled={isLoading || isConnecting}
        >
          {isLoading ? "Loading..." : isConnecting ? "Redirecting..." : "Connect another workspace"}
          <ExternalLink className="ml-2 size-4" />
        </Button>
      </CardFooter>

      <Dialog open={Boolean(pendingDisconnect)} onOpenChange={(open) => !open && setPendingDisconnect(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Disconnect Slack workspace</DialogTitle>
            <DialogDescription>
              {pendingDisconnect
                ? `Disconnect ${pendingDisconnect.teamName || pendingDisconnect.teamId}? This only affects this workspace.`
                : "Disconnect this Slack workspace?"}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setPendingDisconnect(null)}
              disabled={disconnectMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                if (!pendingDisconnect) return;
                disconnectMutation.mutate(pendingDisconnect.teamId);
              }}
              disabled={!pendingDisconnect || disconnectMutation.isPending}
            >
              {disconnectMutation.isPending ? "Disconnecting..." : "Disconnect workspace"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function QuickBooksCard() {
  const [isConnecting, setIsConnecting] = useState(false);
  const [showDisconnectModal, setShowDisconnectModal] = useState(false);

  // Fetch QuickBooks connection status
  const { data: status, isLoading: isLoadingStatus } = useQuery({
    queryKey: ["quickbooks-status"],
    queryFn: getQuickBooksStatus,
    staleTime: 30_000, // Consider data fresh for 30 seconds
    refetchOnWindowFocus: true,
  });

  const isConnected = status?.connected ?? false;

  const handleConnect = () => {
    setIsConnecting(true);
    const coreApiUrl =
      (import.meta.env.VITE_CORE_API_URL as string | undefined) || "http://localhost:3000";
    const redirectPath = "/integrations";
    const connectUrl = `${coreApiUrl}/integrations/quickbooks/connect?redirect=${encodeURIComponent(redirectPath)}`;

    // Redirect to the connect endpoint which will then redirect to QuickBooks
    window.location.href = connectUrl;
  };

  return (
    <Card className="border-l-4 border-l-[#0077C5]">
      <CardHeader>
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-[#0077C5] text-white">
              <span className="text-lg font-bold">QB</span>
            </div>
            <div>
              <CardTitle>QuickBooks</CardTitle>
              <CardDescription>Sync your accounting data</CardDescription>
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-muted-foreground">
          Connect your QuickBooks account to automatically import invoices, expenses, and financial
          reports into your dashboard.
        </p>
        {!isLoadingStatus && (
          <div className="mt-4 rounded-lg border bg-muted/30 p-3">
            {isConnected ? (
              <div className="flex items-center gap-2 text-sm">
                <CheckCircle2 className="size-4 text-emerald-600" />
                <span className="font-medium">Connected</span>
                {status?.realmId && (
                  <span className="text-muted-foreground">({status.realmId})</span>
                )}
              </div>
            ) : (
              <div className="text-sm text-muted-foreground">Not connected</div>
            )}
          </div>
        )}
        {isConnected && (
          <button
            type="button"
            className="mt-2 text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground"
            onClick={() => setShowDisconnectModal(true)}
          >
            Need to disconnect? Contact support
          </button>
        )}
      </CardContent>
      <CardFooter>
        <Button
          variant={isConnected ? "outline" : "default"}
          className="w-full"
          onClick={handleConnect}
          disabled={isConnecting || isLoadingStatus || isConnected}
        >
          {isLoadingStatus
            ? "Loading..."
            : isConnecting
              ? "Redirecting..."
              : isConnected
                ? "Connected"
                : "Connect"}
          <ExternalLink className="ml-2 size-4" />
        </Button>
      </CardFooter>

      <Dialog open={showDisconnectModal} onOpenChange={setShowDisconnectModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Disconnect QuickBooks</DialogTitle>
            <DialogDescription>
              To disconnect your QuickBooks integration, please contact our support team at{" "}
              <a
                href="mailto:support@mono.ai"
                className="text-primary underline hover:no-underline"
              >
                support@mono.ai
              </a>
              .
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDisconnectModal(false)}>
              Close
            </Button>
            <Button
              onClick={() => {
                window.location.href =
                  "mailto:support@mono.ai?subject=QuickBooks Disconnection Request";
                setShowDisconnectModal(false);
              }}
            >
              Contact Support
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
